
import {
  defineComponent, PropType, Ref, ref, watch, onBeforeUnmount, computed,
} from 'vue';
import OverlayPanel from 'primevue/overlaypanel';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';

import {
  ColorFieldConfig, ColorFormatEnum, ParserFieldObject, Property,
} from '@/types';
import { useI18n } from 'vue-i18n';
import CustomColorPicker from '@/components/fields/partials/CustomColorPicker.vue';
import ToggleDisplaySettings from '@/components/template-builder/fields/partials/ToggleDisplaySettings.vue';
import Tooltip from 'primevue/tooltip';

export default defineComponent({
  name: 'Color',
  directives: {
    tooltip: Tooltip,
  },

  components: {
    ToggleDisplaySettings,
    CustomColorPicker,
    OverlayPanel,
    InputText,
    Button,
  },

  props: {
    configs: {
      type: Object as PropType<ColorFieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },

    elementPartType: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const configsValue = ref(props.configs);
    const displayProperties = ref(!(!Object.prototype.hasOwnProperty.call(configsValue.value, 'displayToggle') || configsValue.value.displayToggle === true));
    const VALID_COLOR_REGEX_RULE = /^(#([\da-f]{3}){1,2}$)|(rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(,\s*(\d+|\d*\.\d+))?\s*\))$|((repeating-)?(linear|radial)-gradient\(.*\))$/i;
    const overlayPanelRef = ref();
    const colorFormat: Ref<string> = ref(ColorFormatEnum.HEX);
    const property: Ref<Property> = ref(props.configs.properties && props.configs.properties[0]);
    const selectedColor: Ref<string> = ref(props.configs.options.defaultValue);

    const transparentValues = ['transparent', 'rgba(0, 0, 0, 0)', ''];
    const isTransparent = ref(false);
    const allowTransparent = computed(() => configsValue.value.options.allowTransparent ?? false);
    const transparentValue = computed(() => configsValue.value.options.transparentValue ?? 'transparent');

    const isValidColor = (color: string) => {
      if (allowTransparent.value && transparentValues.includes(color)) {
        return true;
      }
      return VALID_COLOR_REGEX_RULE.test(color);
    };

    const isTransparentValue = (color: string) => transparentValues.includes(color);

    watch(() => props.parserValues, (values, oldValues) => {
      if (values.selector !== oldValues?.selector) {
        if (property.value) {
          if (isTransparentValue(property.value.value) && allowTransparent.value) {
            isTransparent.value = true;
            selectedColor.value = t('templateBuilder.transparent');
          } else {
            isTransparent.value = false;
            selectedColor.value = isValidColor(property.value.value) ? property.value.value : props.configs.options.defaultValue;
          }
        }
      }
    }, { immediate: true });

    const toggleColorPicker = (event: MouseEvent) => {
      overlayPanelRef.value.toggle(event);
    };
    const emitValuesChanges = () => {
      if (property.value.value !== selectedColor.value) {
        property.value.value = selectedColor.value;
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [property.value],
        });
      }
    };
    const changeColor = (color: string) => {
      if (isTransparent.value) {
        isTransparent.value = false;
      }
      selectedColor.value = color;
      emitValuesChanges();
    };
    const filterInputColor = () => {
      if (isValidColor(selectedColor.value)) {
        emitValuesChanges();
      }
    };
    const toggleTransparent = () => {
      isTransparent.value = !isTransparent.value;
      if (isTransparent.value) {
        selectedColor.value = transparentValue.value;
        emitValuesChanges();
        selectedColor.value = t('templateBuilder.transparent');
      } else {
        selectedColor.value = props.configs.options.defaultValue;
        emitValuesChanges();
      }
    };

    onBeforeUnmount(() => {
      overlayPanelRef.value = null;
    });

    return {
      displayProperties,
      selectedColor,
      isTransparent,
      overlayPanelRef,
      colorFormat,
      toggleColorPicker,
      toggleTransparent,
      changeColor,
      filterInputColor,
      configsValue,
      t,
    };
  },
});
